import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Modal, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useToaster } from '../../../../providers/ToasterProvider';
import { submitExceptionDecision } from '../../../../services/deviceExceptionService';

import DROPDOWN_ICON from '../../../../assets/images/dropdown.svg';

const basicInfoFields = [
    { key: 'deviceId', label: 'Device ID' },
    { key: 'macAddress', label: 'MAC Address' },
    { key: 'labNo', label: 'Lab No' },
    { key: 'seatNo', label: 'Seat No' },
    { key: 'hostname', label: 'Hostname' },
    { key: 'kernel', label: 'Kernel' },
];

const humanizeKey = (key) => key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());

const NoData = () => <Typography variant='body2' sx={{ color: 'grey.500' }}>No data available</Typography>;

const RenderValue = ({ value }) => {
    if (value === null || value === undefined || value === '') {
        return <Typography variant='body1'>-</Typography>;
    }
    if (Array.isArray(value)) {
        if (!value.length) {
            return <NoData />;
        }
        return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {value.map((item, index) => (
                <Box key={index} sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                    <RenderValue value={item} />
                </Box>
            ))}
        </Box>;
    }
    if (typeof value === 'object') {
        if (!Object.keys(value).length) {
            return <NoData />;
        }
        return <KeyValueGrid data={value} />;
    }
    return <Typography variant='body1' sx={{ wordBreak: 'break-all' }}>{String(value)}</Typography>;
}

const KeyValueGrid = ({ data }) => (
    <Grid container spacing={1.5}>
        {Object.entries(data).map(([key, value]) => (
            <Grid item xs={6} key={key}>
                <Typography variant='body2' sx={{ color: 'grey. 500' }}>{humanizeKey(key)}</Typography>
                <RenderValue value={value} />
            </Grid>
        ))}
    </Grid>
);

const MemoryStorageSection = ({ memory, storage }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
            <Typography variant='subtitle2' sx={{ mb: 1, color: 'primary.main' }}>Memory</Typography>
            <RenderValue value={memory} />
        </Box>
        <Box>
            <Typography variant='subtitle2' sx={{ mb: 1, color: 'primary.main' }}>Storage</Typography>
            <RenderValue value={storage} />
        </Box>
    </Box>
);

export const DeviceExceptionModal = ({ device, onClose, onDecisionComplete }) => {

    const { addNotification } = useToaster();
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    const handleDecision = async (status) => {
        if (!device) return;
        const setLoading = status === 'approved' ? setIsApproving : setIsRejecting;
        setLoading(true);
        try {
            const res = await submitExceptionDecision(device.deviceId, device.labNo, device.seatNo, status);
            if (res?.code === 'OK') {
                onDecisionComplete(device.deviceId, status);
            }
        } catch (error) {
            addNotification(`Unable to ${status === 'approved' ? 'approve' : 'reject'} device exception.`, 'danger');
            console.log('There is an error while submitting device exception decision', error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal open={Boolean(device)} hideBackdrop={true} className='backdrop' onClose={onClose}>
            <Box className='modal-container' sx={{ maxWidth: 900, p: 3 }}>
                <Typography variant='h5' component='h5' sx={{ mb: 2.5, color: 'primary.main' }}>
                    Device Exception Details
                </Typography>

                {device && <>
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}>
                            <Typography sx={{ fontWeight: 500 }}>Basic Info</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={1.5}>
                                {basicInfoFields.map((field) => (
                                    <Grid item xs={field.key === 'deviceId' ? 12 : 6} key={field.key}>
                                        <Typography variant='body2' sx={{ color: 'grey.500' }}>{field.label}</Typography>
                                        <RenderValue value={device[field.key]} />
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}>
                            <Typography sx={{ fontWeight: 500 }}>PCI</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <RenderValue value={device.pci} />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}>
                            <Typography sx={{ fontWeight: 500 }}>Memory Storage</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <MemoryStorageSection memory={device.memory} storage={device.storage} />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}>
                            <Typography sx={{ fontWeight: 500 }}>BIOS</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <RenderValue value={device.bios} />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<img src={DROPDOWN_ICON} alt='' />}>
                            <Typography sx={{ fontWeight: 500 }}>Firmware</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <RenderValue value={device.firmware} />
                        </AccordionDetails>
                    </Accordion>

                    <Box className='actions-container' sx={{ mt: 3, justifyContent: 'center', gap: '40px' }}>
                        <LoadingButton
                            sx={{ borderRadius: 40 }}
                            color='secondary'
                            variant='contained'
                            onClick={() => handleDecision('rejected')}
                            loading={isRejecting}
                            disabled={isApproving}
                        >
                            Reject
                        </LoadingButton>
                        <LoadingButton
                            sx={{ borderRadius: 40 }}
                            color='secondary'
                            variant='contained'
                            onClick={() => handleDecision('approved')}
                            loading={isApproving}
                            disabled={isRejecting}
                        >
                            Approve
                        </LoadingButton>
                    </Box>
                </>}
            </Box>
        </Modal>
    );
}
